import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Bell,
  Loader2,
  CheckCircle2,
  ArrowRight,
  Activity,
  MessageSquare,
  ListTodo,
} from "lucide-react";

import { tarefaService } from "@/services/api/tarefa.service";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth-context";
import { TarefaDTO } from "@/types/tarefa.type";

import { TarefaNotificacaoItem } from "./TarefaNotificacaoItem";

const MAX_TAREFAS_RECENTES = 10;

function NotificacoesLista({
  tarefas,
  isLoading,
  userId,
  mensagemNaoLida = false,
  onNavigate,
}: {
  tarefas: TarefaDTO[];
  isLoading: boolean;
  userId?: string | number;
  mensagemNaoLida?: boolean;
  onNavigate: () => void;
}) {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center space-y-3 py-10">
        <Loader2 className="h-6 w-6 animate-spin text-primary/60" />
        <span className="text-xs font-medium text-muted-foreground">Sincronizando...</span>
      </div>
    );
  }

  if (tarefas.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center space-y-3 px-4 py-12 text-center duration-500 animate-in fade-in">
        <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10">
          <CheckCircle2 className="h-6 w-6 text-emerald-500" />
        </div>
        <p className="text-sm font-semibold text-foreground">Você está em dia!</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col divide-y divide-border/40">
      {tarefas.map((tarefa, index) => (
        <TarefaNotificacaoItem
          key={tarefa.id}
          tarefa={tarefa}
          userId={userId}
          index={index}
          mensagemNaoLida={mensagemNaoLida}
          onNavigate={onNavigate}
        />
      ))}
    </div>
  );
}

export function TarefaNotificacoes() {
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();

  const { data: count = 0 } = useQuery({
    queryKey: ["tarefas-pendentes-count"],
    queryFn: () => tarefaService.countPendentes(),
    refetchInterval: 60 * 1000,
  });

  // Mesmo cadência do badge (refetchInterval 60s): sem isso, o badge se atualiza
  // sozinho mas a lista fica presa no snapshot de quando o popover foi aberto pela
  // última vez — alguém podia ver "3" no sino e a lista vazia até fechar/reabrir.
  const { data: notificacoes, isLoading } = useQuery({
    queryKey: ["tarefas-notificacoes"],
    queryFn: () => tarefaService.getNotificacoes(MAX_TAREFAS_RECENTES),
    enabled: isOpen,
    staleTime: 1000 * 30,
    refetchInterval: isOpen ? 60 * 1000 : false,
  });

  const hasNotifications = count > 0;

  const handleClose = () => setIsOpen(false);

  const tarefasPendentes = notificacoes?.pendentes ?? [];
  const tarefasComMensagem = notificacoes?.mensagens ?? [];

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger
        className={buttonVariants({
          variant: "ghost",
          size: "icon",
          className: cn(
            "relative h-9 w-9 rounded-full transition-all duration-200 hover:bg-muted focus-visible:ring-1",
            isOpen ? "bg-muted text-primary" : "text-muted-foreground",
          ),
        })}
      >
        <Bell className={cn("h-5 w-5", hasNotifications && "animate-in slide-in-from-top-1")} />
        {hasNotifications && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-bold text-destructive-foreground shadow-sm ring-2 ring-background animate-in zoom-in">
            {count > 99 ? "99+" : count}
          </span>
        )}
        <span className="sr-only">Notificações de Tarefas</span>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[calc(100vw-4rem)] overflow-hidden rounded-xl border-border/50 bg-background p-0 shadow-xl sm:w-80 md:w-96"
      >
        {/* Header */}
        <div className="flex items-center gap-2 border-b border-border/50 bg-card px-4 py-3">
          <div className="rounded-md bg-primary/10 p-1.5">
            <Activity className="h-4 w-4 text-primary" />
          </div>
          <h4 className="text-sm font-semibold text-foreground">Tarefas Recentes</h4>
        </div>

        {/* Content Area */}
        <Tabs defaultValue="pendentes" className="gap-0">
          <div className="border-b border-border/50 bg-muted/30 px-4 py-2">
            <TabsList className="w-full">
              <TabsTrigger value="pendentes" className="flex-1 gap-1.5">
                <ListTodo className="h-3.5 w-3.5" />
                Pendentes {tarefasPendentes.length > 0 && `(${tarefasPendentes.length})`}
              </TabsTrigger>
              <TabsTrigger value="mensagens" className="flex-1 gap-1.5">
                <MessageSquare className="h-3.5 w-3.5" />
                Mensagens {tarefasComMensagem.length > 0 && `(${tarefasComMensagem.length})`}
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="custom-scrollbar flex max-h-[50vh] flex-col overflow-y-auto bg-muted/5 sm:max-h-[350px]">
            <TabsContent value="pendentes" className="mt-0">
              <NotificacoesLista
                tarefas={tarefasPendentes}
                isLoading={isLoading}
                userId={user?.id}
                onNavigate={handleClose}
              />
            </TabsContent>
            <TabsContent value="mensagens" className="mt-0">
              <NotificacoesLista
                tarefas={tarefasComMensagem}
                isLoading={isLoading}
                userId={user?.id}
                mensagemNaoLida
                onNavigate={handleClose}
              />
            </TabsContent>
          </div>
        </Tabs>

        {/* Footer */}
        <div className="border-t border-border/50 bg-card p-2">
          <Link
            className={buttonVariants({
              variant: "ghost",
              className:
                "group flex h-9 w-full items-center justify-center gap-1.5 text-xs font-semibold text-primary hover:bg-primary/10 hover:text-primary",
            })}
            to="/tarefas"
            onClick={handleClose}
          >
            Ver todas as tarefas
            <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
